/**
 * Canonical feature-owned managed-column requirements.
 *
 * This module deliberately contains no filesystem, process, network, or
 * user-module access. Callers supply the project facts that determine which
 * requirements apply to each discovered table.
 */

export const SCHEMA_ENGINE_VERSION = '2026-07-28.1';

export const SQLITE_EPOCH_SENTINEL = `'1970-01-01T00:00:00.000Z'`;
export const SQLITE_ISO_NOW_FN = `() => new Date().toISOString()`;

export type SchemaDialect = 'postgres' | 'sqlite';
export type ManagedColumnDialect = SchemaDialect;
export type NamingConvention = 'snake_case' | 'camelCase';
export type ManagedRequirementKind = 'audit' | 'soft-delete' | 'id' | 'scope';

export const AUDIT_JS_NAMES = ['createdAt', 'modifiedAt', 'createdBy', 'modifiedBy'] as const;
export const SOFT_DELETE_JS_NAMES = ['deletedAt', 'deletedBy'] as const;

export type AuditJsName = (typeof AUDIT_JS_NAMES)[number];
export type SoftDeleteJsName = (typeof SOFT_DELETE_JS_NAMES)[number];
export type AuditManagedJsName = AuditJsName | SoftDeleteJsName;

export interface ManagedColumnSpec {
  jsName: string;
  snakeName: string;
  kind: 'timestamp' | 'text';
  notNull: boolean;
  /** Compatibility discriminator used by existing compiler consumers. */
  audit: boolean;
  requirement: ManagedRequirementKind;
}

export const AUDIT_COLUMN_SPECS: ReadonlyArray<ManagedColumnSpec> = [
  {
    jsName: 'createdAt',
    snakeName: 'created_at',
    kind: 'timestamp',
    notNull: true,
    audit: true,
    requirement: 'audit',
  },
  {
    jsName: 'modifiedAt',
    snakeName: 'modified_at',
    kind: 'timestamp',
    notNull: true,
    audit: true,
    requirement: 'audit',
  },
  {
    jsName: 'createdBy',
    snakeName: 'created_by',
    kind: 'text',
    notNull: false,
    audit: true,
    requirement: 'audit',
  },
  {
    jsName: 'modifiedBy',
    snakeName: 'modified_by',
    kind: 'text',
    notNull: false,
    audit: true,
    requirement: 'audit',
  },
];

export const SOFT_DELETE_COLUMN_SPECS: ReadonlyArray<ManagedColumnSpec> = [
  {
    jsName: 'deletedAt',
    snakeName: 'deleted_at',
    kind: 'timestamp',
    notNull: false,
    audit: false,
    requirement: 'soft-delete',
  },
  {
    jsName: 'deletedBy',
    snakeName: 'deleted_by',
    kind: 'text',
    notNull: false,
    audit: false,
    requirement: 'soft-delete',
  },
];

export const MANAGED_AUDIT_COLUMN_SPECS: ReadonlyArray<ManagedColumnSpec> = [
  ...AUDIT_COLUMN_SPECS,
  ...SOFT_DELETE_COLUMN_SPECS,
];
export const MANAGED_COLUMN_SPECS = MANAGED_AUDIT_COLUMN_SPECS;
export const MANAGED_AUDIT_JS_NAMES: ReadonlySet<string> = new Set([
  ...AUDIT_JS_NAMES,
  ...SOFT_DELETE_JS_NAMES,
]);

export interface ManagedGroupRequirement {
  kind: 'audit' | 'soft-delete';
  columns: ReadonlyArray<ManagedColumnSpec>;
  /** Canonical q-DSL spread helper, without the leading `...` or call. */
  qHelper: 'audit' | 'softDelete';
}

export interface ManagedPhysicalColumnRequirement {
  kind: 'id' | 'scope';
  jsName: string;
  sqlName: string;
  /** Canonical q initializer, without `<jsName>:`. */
  qExpression: string;
  /** Canonical raw-Drizzle initializer, without `<jsName>:`. */
  rawExpression: string;
  /**
   * Additional exact expressions accepted as canonical. This is useful while
   * source-visible ID strategies are migrated without weakening validation.
   */
  acceptedQExpressions?: ReadonlyArray<string>;
  acceptedRawExpressions?: ReadonlyArray<string>;
}

export type ManagedColumnRequirement =
  | ManagedGroupRequirement
  | ManagedPhysicalColumnRequirement;

export interface IdRequirementOptions {
  dialect: SchemaDialect;
  namingConvention?: NamingConvention;
  qExpression?: string;
  rawExpression?: string;
  acceptedQExpressions?: ReadonlyArray<string>;
  acceptedRawExpressions?: ReadonlyArray<string>;
}

export interface ScopeRequirementOptions {
  dialect: SchemaDialect;
  kind: string;
  jsName?: string;
  sqlName?: string;
  namingConvention?: NamingConvention;
  qExpression?: string;
  rawExpression?: string;
  acceptedQExpressions?: ReadonlyArray<string>;
  acceptedRawExpressions?: ReadonlyArray<string>;
}

export function managedSqlName(
  spec: Pick<ManagedColumnSpec, 'jsName' | 'snakeName'>,
  convention: NamingConvention,
): string {
  return convention === 'snake_case' ? spec.snakeName : spec.jsName;
}

export function managedColumnExpr(
  spec: ManagedColumnSpec,
  dialect: SchemaDialect,
  sqlName: string,
): string {
  if (spec.kind === 'text' || (spec.jsName === 'deletedAt' && dialect === 'sqlite')) {
    return dialect === 'postgres' && spec.kind === 'timestamp'
      ? `timestamp("${sqlName}", { withTimezone: true })`
      : `text("${sqlName}")`;
  }
  if (spec.jsName === 'deletedAt') {
    return `timestamp("${sqlName}", { withTimezone: true })`;
  }
  const pgUpdate = spec.jsName === 'modifiedAt' ? `.$onUpdate(() => new Date())` : '';
  const sqliteUpdate =
    spec.jsName === 'modifiedAt' ? `.$onUpdate(${SQLITE_ISO_NOW_FN})` : '';
  return dialect === 'postgres'
    ? `timestamp("${sqlName}", { withTimezone: true }).notNull().defaultNow()${pgUpdate}`
    : `text("${sqlName}").notNull().default(${SQLITE_EPOCH_SENTINEL}).$defaultFn(${SQLITE_ISO_NOW_FN})${sqliteUpdate}`;
}

export function managedColumnLine(
  spec: ManagedColumnSpec,
  dialect: SchemaDialect,
  convention: NamingConvention,
): string {
  return `${spec.jsName}: ${managedColumnExpr(
    spec,
    dialect,
    managedSqlName(spec, convention),
  )},`;
}

export function auditRequirement(): ManagedGroupRequirement {
  return {
    kind: 'audit',
    columns: AUDIT_COLUMN_SPECS,
    qHelper: 'audit',
  };
}

export function softDeleteRequirement(): ManagedGroupRequirement {
  return {
    kind: 'soft-delete',
    columns: SOFT_DELETE_COLUMN_SPECS,
    qHelper: 'softDelete',
  };
}

export function idRequirement(options: IdRequirementOptions): ManagedPhysicalColumnRequirement {
  const sqlName = options.namingConvention === 'camelCase' ? 'id' : 'id';
  return {
    kind: 'id',
    jsName: 'id',
    sqlName,
    qExpression: options.qExpression ?? 'q.id()',
    rawExpression: options.rawExpression ?? `text("${sqlName}").primaryKey()`,
    acceptedQExpressions: options.acceptedQExpressions,
    acceptedRawExpressions: options.acceptedRawExpressions,
  };
}

export function scopeRequirement(
  options: ScopeRequirementOptions,
): ManagedPhysicalColumnRequirement {
  const jsName = options.jsName ?? `${options.kind}Id`;
  const convention = options.namingConvention ?? 'snake_case';
  const snakeName = jsName.replace(/[A-Z]/g, (character) => `_${character.toLowerCase()}`);
  const sqlName = options.sqlName ?? (convention === 'snake_case' ? snakeName : jsName);
  return {
    kind: 'scope',
    jsName,
    sqlName,
    qExpression: options.qExpression ?? `q.scope("${options.kind}")`,
    rawExpression: options.rawExpression ?? `text("${sqlName}").notNull()`,
    acceptedQExpressions: options.acceptedQExpressions,
    acceptedRawExpressions: options.acceptedRawExpressions,
  };
}
